import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import type { WebFlowRoutingProps } from "../navigation/webFlowRouting";

/**
 * Keeps the active step index aligned with `/flow/:stepSlug` on web.
 * Uses replace navigation from the parent to avoid bloating history on every micro-transition.
 *
 * Important: we only apply URL → step when the **route slug** changes (back/forward, deep link).
 * If we re-ran whenever `stepDefinitions` changed while the URL was still the old slug, we would
 * reset `currentStep` to match the stale URL — e.g. after `setSteps(BASE_STEPS_EXISTING)` the URL
 * might still be `phone-number` for a frame while state has already moved to PIN/country, causing
 * flicker and stuck steps.
 */
export function useWebFlowStepSync(
  webFlowRouting: WebFlowRoutingProps | undefined,
  stepDefinitions: { key: string }[],
  currentStep: number,
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
): void {
  const [urlHydrated, setUrlHydrated] = useState(!webFlowRouting);
  const lastSyncedSlugRef = useRef<string | undefined>(undefined);

  // URL → step index (browser back/forward, deep link, or parent redirect)
  useEffect(() => {
    if (Platform.OS !== "web" || !webFlowRouting) {
      setUrlHydrated(true);
      return;
    }
    const slug = webFlowRouting.stepSlug;
    // Same slug we already applied to state — only `stepDefinitions` may have changed (e.g.
    // BASE_STEPS_NEW → BASE_STEPS_EXISTING). Do not reset currentStep to match stale URL.
    if (lastSyncedSlugRef.current === slug) {
      setUrlHydrated(true);
      return;
    }

    const key = webFlowRouting.mapSlugToStepKey(slug);
    if (!key) {
      setUrlHydrated(true);
      return;
    }
    const idx = stepDefinitions.findIndex((s) => s.key === key);
    if (idx < 0) {
      setUrlHydrated(true);
      return;
    }
    setCurrentStep((prev) => (prev === idx ? prev : idx));
    lastSyncedSlugRef.current = slug;
    setUrlHydrated(true);
  }, [webFlowRouting?.stepSlug, stepDefinitions, setCurrentStep, webFlowRouting]);

  // Step index → URL
  useEffect(() => {
    if (Platform.OS !== "web" || !webFlowRouting || !urlHydrated) return;
    const key = stepDefinitions[currentStep]?.key;
    if (!key) return;
    const slug = webFlowRouting.mapStepKeyToSlug(key);
    if (!slug) return;
    if (slug === webFlowRouting.stepSlug) return;
    webFlowRouting.onStepSlugChange(slug);
  }, [
    currentStep,
    stepDefinitions,
    urlHydrated,
    webFlowRouting?.stepSlug,
    webFlowRouting?.mapStepKeyToSlug,
    webFlowRouting?.onStepSlugChange,
  ]);
}
